/**
 * Library endpoint handlers for app purchases and delivery
 */

import { GPNilPayloadError, GPMissingAppDeliveryDataError } from "../../errors";
import { URL_PURCHASE, URL_DELIVERY } from "../constants";
import type { 
    BuyResponse, 
    DeliveryResponse, 
    AndroidAppDeliveryData,
    Payload 
} from "../../gen_proto/googleplay_pb";

export class LibraryEndpoint {
    constructor(
        private doAuthedReq: (url: string, options?: RequestInit) => Promise<Payload | null>,
        private getAppDetails: (packageName: string) => Promise<{ versionCode: number }>
    ) {}

    async getBuyResponse(packageName: string, version: number): Promise<BuyResponse> {
        const params = new URLSearchParams();
        params.set("ot", "1");
        params.set("doc", packageName);
        params.set("vc", version.toString());

        const payload = await this.doAuthedReq(`${URL_PURCHASE}?${params.toString()}`, {
            method: "POST",
        });

        if (!payload) {
            throw new GPNilPayloadError();
        }

        if (!payload.buyResponse) {
            throw new GPNilPayloadError("Buy response is missing");
        }

        return payload.buyResponse;
    }

    async getDeliveryResponse(packageName: string, version: number): Promise<DeliveryResponse> {
        const params = new URLSearchParams();
        params.set("ot", "1");
        params.set("doc", packageName);
        params.set("vc", version.toString());

        const payload = await this.doAuthedReq(`${URL_DELIVERY}?${params.toString()}`, {
            method: "GET",
        });

        if (!payload) {
            throw new GPNilPayloadError();
        }

        if (!payload.deliveryResponse) {
            throw new GPNilPayloadError("Delivery response is missing");
        }

        return payload.deliveryResponse;
    }

    async purchase(packageName: string, version?: number): Promise<AndroidAppDeliveryData> {
        let actualVersion = version;
        
        if (!actualVersion) {
            const app = await this.getAppDetails(packageName);
            actualVersion = app.versionCode;
        }

        // First, make the buy request
        await this.getBuyResponse(packageName, actualVersion);

        // Then, get the delivery response
        const deliveryResponse = await this.getDeliveryResponse(packageName, actualVersion);

        const appDeliveryData = deliveryResponse.appDeliveryData;
        if (!appDeliveryData) {
            throw new GPMissingAppDeliveryDataError();
        }

        return appDeliveryData;
    }
}
